import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, CartesianGrid } from "recharts";

function Stat({ label, value, unit }) {
  return (
    <div>
      <div className="label-overline">{label}</div>
      <div className="font-serif-display text-4xl md:text-5xl text-[color:var(--parchment)] tracking-tight mt-1">
        {value}
        {unit && <span className="text-[color:var(--text-secondary)] text-base font-sans-body ml-1">{unit}</span>}
      </div>
    </div>
  );
}

function statusColor(status) {
  if (status === "storm" || status === "severe storm") return "var(--accent-warning)";
  if (status === "active" || status === "unsettled") return "var(--rose-gold)";
  return "var(--gold)";
}

export default function SchumannPanel({ current, history }) {
  const data = (history?.points || []).slice(-72).map((p, i) => ({
    idx: i,
    freq: p.freq,
    amp: p.amp,
  }));
  const color = current ? statusColor(current.status) : "var(--gold)";

  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10 h-full hover-lift" data-testid="schumann-panel">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-overline constellation-underline">Schumann Resonance</div>
          <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight">
            Earth&apos;s heartbeat
          </h2>
          <p className="font-serif-italic text-[color:var(--text-secondary)] mt-1">baseline 7.83 Hz</p>
        </div>
        <div
          className="px-3 py-1 rounded-full border font-serif-italic text-sm capitalize"
          style={{ borderColor: color, color }}
          data-testid="schumann-status-badge"
        >
          {current?.status || "—"}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <Stat label="Frequency" value={current ? current.base_freq_hz.toFixed(2) : "—"} unit="Hz" />
        <Stat label="Amplitude" value={current ? current.amplitude.toFixed(1) : "—"} unit="pT" />
        <Stat label="Activity" value={current ? current.activity_index : "—"} unit="/100" />
      </div>

      <div
        className="relative h-56 md:h-60 rounded-xl overflow-hidden border border-[color:var(--gold)]/10"
        style={{ background: "rgba(5, 9, 23, 0.6)" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 14, right: 14, left: 4, bottom: 6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(212, 162, 76, 0.08)" />
            <XAxis dataKey="idx" hide />
            <YAxis
              domain={["auto", "auto"]}
              stroke="rgba(184, 175, 161, 0.5)"
              tick={{ fontFamily: "Instrument Sans", fontSize: 10, fill: "#7a7565" }}
              width={30}
            />
            <Tooltip
              contentStyle={{
                background: "rgba(19, 27, 54, 0.96)",
                border: "1px solid rgba(212, 162, 76, 0.3)",
                borderRadius: 6,
                fontFamily: "Instrument Sans",
                fontSize: 12,
                color: "#f8f1e4",
              }}
              labelStyle={{ color: "#b8afa1" }}
              itemStyle={{ color }}
              formatter={(v) => v.toFixed(2)}
            />
            <Line
              type="monotone"
              dataKey="amp"
              stroke={color}
              strokeWidth={1.8}
              dot={false}
              isAnimationActive={false}
              filter="drop-shadow(0 0 6px rgba(212, 162, 76, 0.35))"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 flex items-center justify-between label-overline">
        <span>Source · {current?.source || "—"}</span>
        <span>last 72 hours</span>
      </div>
    </div>
  );
}
